/**
 * «MCP від inFLOW» — підключення до ChatGPT, Claude, Cursor
 * (owner 2026-10-30: «люди просили API або MCP, щоб працювати з inFLOW в ШІ»).
 *
 * Людина вмикає підключення й отримує особисте посилання — його вставляють у
 * налаштування конекторів ШІ. Через MCP ШІ бачить вакансії її сфер із денним
 * лімітом і повний текст тих, які людина відкрила, — без контактів: вони
 * тільки в застосунку (owner 2026-10-31: «через MCP нельзя ничего забрать»;
 * 2026-11-01: «улучшай, чтоб было полезно»). Сервер —
 * `src/inflow/api/routers/mcp.py`.
 */
import { useEffect, useState, type ReactNode } from "react";
import { BackNav } from "@/components/BackNav";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/Section";
import { ActionButton } from "@/components/ActionButton";
import { Icon, type IconName } from "@/components/Icon";
import { LockedSectionPopup } from "@/components/LockedSectionPopup";
import { CenteredError, Spinner } from "@/components/Spinner";
import { ChatGptAppMock, ClaudeConnectorMock } from "@/components/McpConnectorMocks";
import { useChangeMcpConnection, useMcpConnection, useMySubscription } from "@/api/hooks";
import { isActiveSubscription } from "@/api/types";
import { showAppAlert } from "@/lib/appAlert";
import { showToast } from "@/lib/toast";
import { tg } from "@/lib/telegram";
import { TITLES } from "@/lib/appMap";

/** Памʼять пристрою: людина вже відкривала сторінку — бейдж «new» на Головній гасне. */
export const MCP_PAGE_SEEN_KEY = "mcp_page_seen";

/** Без підписки на день — як `DAILY_VACANCIES_FREE` і `FULL_TEXTS_FREE` на сервері. */
const FREE_DAILY_VACANCIES = 15;
const FREE_FULL_TEXTS = 5;

/** Що вміє MCP від inFLOW (owner 2026-11-01: «описание раздела и возможностей»). */
const ABILITIES: { icon: IconName; title: string; text: string; paid?: boolean }[] = [
  {
    icon: "card-search",
    title: "Шукає вакансії за запитом",
    text: "Напиши своїми словами, наприклад «лендинг до 10 000 ₴», і ШІ знайде такі вакансії у твоїх сферах.",
  },
  {
    icon: "calendar",
    title: "Показує свіжу стрічку",
    text: "Нові вакансії твоїх сфер за останні 7 днів.",
  },
  {
    icon: "clipboard-check",
    title: "Розбирає вакансію",
    text: "Суть завдання, бюджет, оцінка замовника і чи варто братися.",
  },
  {
    icon: "pen-square",
    title: "Пише відгук",
    text: "Чернетка відповіді замовнику з урахуванням твого профілю і портфоліо.",
  },
  {
    icon: "folder-bookmark",
    title: "Зберігає вакансії",
    text: "Додає вакансії до розділу «Збережені», щоб повернутися до них пізніше.",
    paid: true,
  },
];

type Client = "claude" | "chatgpt" | "cursor" | "other";

/** Claude першим: він підключається й на телефоні, а ChatGPT — лише в браузері на компʼютері. */
const CLIENT_LABEL: Record<Client, string> = {
  claude: "Claude",
  chatgpt: "ChatGPT",
  cursor: "Cursor",
  other: "Інші",
};

/** Попап «Перевипустити посилання?» — і на сторінці, і в галереї попапів. */
export function confirmMcpReissue(onConfirm: () => void) {
  showAppAlert(
    "Старе посилання одразу перестане працювати. У ChatGPT, Claude чи іншому ШІ доведеться вставити нове.",
    {
      title: "Перевипустити посилання?",
      emoji: "🔑",
      buttons: [
        { label: "Перевипустити", variant: "primary", onClick: onConfirm },
        { label: "Скасувати", variant: "ghost" },
      ],
    },
  );
}

/** Попап «Вимкнути підключення?» — і на сторінці, і в галереї попапів. */
export function confirmMcpDisable(onConfirm: () => void) {
  showAppAlert(
    "ШІ втратить доступ до inFLOW. Увімкнути підключення знову можна будь-коли — посилання залишиться тим самим.",
    {
      title: "Вимкнути підключення?",
      emoji: "🔐",
      buttons: [
        { label: "Вимкнути", variant: "danger", onClick: onConfirm },
        { label: "Скасувати", variant: "ghost" },
      ],
    },
  );
}

async function copy(text: string, done: string) {
  try {
    if (!navigator.clipboard) throw new Error("no clipboard");
    await navigator.clipboard.writeText(text);
    tg()?.HapticFeedback?.notificationOccurred?.("success");
    showToast(done, { emoji: "✓" });
  } catch {
    showAppAlert("Не вдалося скопіювати. Виділи текст і скопіюй його вручну.");
  }
}

function openOutside(url: string) {
  const webapp = tg();
  if (webapp?.openLink) webapp.openLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("uk-UA", { day: "numeric", month: "long" });
  const time = d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });
  return `${date} о ${time}`;
}

/** Назва з `clientInfo` (openai-mcp, Anthropic, claude-ai…) — як її знає людина.
 *  Так само рахує адмінка: `services/mcp_stats.client_label`. */
function clientLabel(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.toLowerCase();
  if (s.includes("openai") || s.includes("chatgpt")) return "ChatGPT";
  if (s.includes("claude") || s.includes("anthropic")) return "Claude";
  if (s.includes("cursor")) return "Cursor";
  return raw;
}

export function McpConnectPage() {
  const conn = useMcpConnection();
  const change = useChangeMcpConnection();
  const sub = useMySubscription();
  const [client, setClient] = useState<Client>("claude");
  const [savedLocked, setSavedLocked] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem(MCP_PAGE_SEEN_KEY, "1");
    } catch {
      /* немає доступу до памʼяті — бейдж просто лишиться */
    }
  }, []);

  if (conn.isLoading) return <Spinner />;
  if (conn.isError || !conn.data) return <CenteredError message="Не вдалося завантажити. Спробуй ще раз." />;

  const c = conn.data;
  const url = c.enabled ? c.url ?? "" : "";
  const isPaid = sub.data ? isActiveSubscription(sub.data) && sub.data.status === "active" : false;
  const isTest = !!sub.data && isActiveSubscription(sub.data) && sub.data.plan === "test";
  const app = clientLabel(c.last_client);

  const run = (action: "enable" | "reissue" | "disable", done?: string) =>
    change.mutate(action, {
      onSuccess: (res) => {
        if (action !== "disable" && !res.enabled) return; // сторож ще не відпустив — сторінка покаже, до коли
        tg()?.HapticFeedback?.notificationOccurred?.("success");
        if (done) showToast(done, { emoji: "✓" });
      },
      onError: (e) => showAppAlert((e as Error).message),
    });

  const askReissue = () => confirmMcpReissue(() => run("reissue", "Нове посилання готове"));
  const askDisable = () => confirmMcpDisable(() => run("disable", "Підключення вимкнено"));

  return (
    <>
      <BackNav />

      <PageHeader
        eyebrow="інтеграції"
        title={TITLES.mcp}
        titleLead="MCP від"
        titleAccent="inFLOW."
        subtitle="Підключи inFLOW до ChatGPT, Claude чи Cursor і шукай вакансії прямо в чаті з ШІ."
      />

      <Section title="Що вміє MCP від inFLOW" bodyClassName="p-0">
        {ABILITIES.map((a, i) => (
          <Ability
            key={a.icon}
            {...a}
            locked={!!a.paid && !isPaid}
            last={i === ABILITIES.length - 1}
            onLocked={() => setSavedLocked(true)}
          />
        ))}
      </Section>

      <Section
        title="Твоє посилання"
        bodyClassName="p-4"
        footer={
          c.enabled
            ? isPaid
              ? "Контакти замовників ШІ не бачить: вони відкриваються в inFLOW."
              : `Без підписки ШІ бачить ${FREE_DAILY_VACANCIES} вакансій на день і повний текст ${FREE_FULL_TEXTS} із них, а контакти замовників відкриваються в inFLOW.`
            : undefined
        }
      >
        {c.enabled ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-[0.75rem] leading-snug text-ink-dim">
              <span
                className={
                  "h-[0.4375rem] w-[0.4375rem] shrink-0 rounded-full " + (c.last_used_at ? "bg-success" : "bg-ink/20")
                }
              />
              <span className="min-w-0 truncate">
                {c.last_used_at
                  ? `Підключено${app ? ` до ${app}` : ""} · ${whenLabel(c.last_used_at)}`
                  : "ШІ ще не підключено"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => void copy(url, "Посилання скопійовано")}
              className="rounded-[0.75rem] bg-bg px-3 py-2.5 text-left font-mono text-[0.71875rem] leading-snug text-ink [font-variant-ligatures:none] [overflow-wrap:anywhere]"
            >
              {url}
            </button>
            <ActionButton onClick={() => void copy(url, "Посилання скопійовано")}>
              <Icon name="copy" size={16} color="#FFFFFF" />
              Скопіювати посилання
            </ActionButton>
            <div className="flex justify-center gap-6 pt-0.5 font-display text-[0.75rem] font-semibold">
              <button type="button" onClick={askReissue} disabled={change.isPending} className="text-primary">
                Нове посилання
              </button>
              <button type="button" onClick={askDisable} disabled={change.isPending} className="text-ink-dim">
                Вимкнути
              </button>
            </div>
          </div>
        ) : c.blocked_until ? (
          <div className="text-[0.8125rem] leading-snug text-ink [text-wrap:balance]">
            Підключення вимкнено через незвичну активність: посилання працювало як програма, а не як людина.
            Увімкнути його знову можна буде {whenLabel(c.blocked_until)}, посилання буде нове.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="text-[0.8125rem] leading-snug text-ink-dim [text-wrap:balance]">
              Отримай особисте посилання й додай його в ШІ. Це займає дві хвилини.
            </div>
            <ActionButton onClick={() => run("enable", "Посилання готове")} disabled={change.isPending}>
              Отримати посилання
            </ActionButton>
          </div>
        )}
      </Section>

      <Section title="Як підключити" bodyClassName="p-4">
        <div className="mb-4 flex gap-1 rounded-full bg-bg p-1">
          {(Object.keys(CLIENT_LABEL) as Client[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                tg()?.HapticFeedback?.selectionChanged?.();
                setClient(k);
              }}
              className={
                "flex-1 rounded-full py-1.5 font-display text-[0.75rem] font-semibold transition " +
                (client === k ? "bg-surface text-ink shadow-sm" : "text-ink-faint active:bg-ink/5")
              }
            >
              {CLIENT_LABEL[k]}
            </button>
          ))}
        </div>
        <ClientGuide client={client} url={url} />
        {c.enabled && (
        <div className="mt-4 rounded-[0.75rem] bg-bg px-3 py-2.5 text-[0.75rem] leading-snug text-ink-dim [text-wrap:balance]">
          <b className="font-semibold text-ink">Посилання особисте.</b> Воно працює без пароля, тому нікому його не
          передавай. Якщо воно потрапило до когось іще, натисни «Нове посилання» — старе одразу перестане працювати.
        </div>
        )}
      </Section>

      <LockedSectionPopup
        sectionKey={savedLocked ? "saved" : null}
        open={savedLocked}
        onClose={() => setSavedLocked(false)}
        isTest={isTest}
      />
    </>
  );
}

/** Рядок «Що вміє»: плитка + назва + опис. Платне без підписки — сіре й під
 *  замком, як рядки розділів на Головній (правило «для безкоштовних не ховаємо»). */
function Ability({
  icon,
  title,
  text,
  locked,
  last,
  onLocked,
}: {
  icon: IconName;
  title: string;
  text: string;
  locked: boolean;
  last: boolean;
  onLocked: () => void;
}) {
  const body = (
    <>
      <div
        className={`grid h-10 w-10 shrink-0 place-items-center rounded-[0.75rem] ${
          locked ? "bg-ink/[0.05]" : "bg-primary/10 text-primary"
        }`}
      >
        <Icon name={icon} size={16} color={locked ? "#A8A29C" : undefined} />
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={`font-display text-[0.90625rem] font-semibold leading-tight ${
            locked ? "text-ink-dim" : "text-ink"
          }`}
        >
          {title}
        </div>
        <div
          className={`mt-0.5 text-[0.71875rem] leading-snug [text-wrap:balance] ${
            locked ? "text-ink-faint" : "text-ink-dim"
          }`}
        >
          {text}
        </div>
      </div>
      {locked && <Icon name="lock" size={14} color="#A8A29C" />}
    </>
  );
  const cls = `flex w-full items-center gap-3 px-4 py-3.5 text-left ${last ? "" : "border-b border-line"}`;
  return locked ? (
    <button type="button" onClick={onLocked} className={`${cls} active:bg-surface-soft`}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** Назва кнопки чи поля в чужому застосунку — жирним, щоб око чіплялось. */
function B({ children }: { children: ReactNode }) {
  return <b className="font-semibold">«{children}»</b>;
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="flex flex-col gap-2.5">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 text-[0.8125rem] leading-snug text-ink">
          <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-[0.6875rem] font-bold text-primary">
            {i + 1}
          </span>
          <span className="pt-px [text-wrap:balance]">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function CodeLine({ label, code, disabled }: { label: string; code: string; disabled: boolean }) {
  return (
    <div className="mt-3">
      <div className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-ink-dim">{label}</div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => void copy(code, "Скопійовано")}
        className="flex w-full items-start gap-2 rounded-[0.75rem] bg-bg px-3 py-2.5 text-left disabled:opacity-50"
      >
        <span className="flex-1 whitespace-pre-wrap font-mono text-[0.6875rem] leading-snug text-ink [font-variant-ligatures:none] [overflow-wrap:anywhere]">
          {code}
        </span>
        <Icon name="copy" size={14} color="#A8A29C" className="mt-0.5 shrink-0" />
      </button>
    </div>
  );
}

function ClientGuide({ client, url }: { client: Client; url: string }) {
  const u = url || "<твоє посилання>";
  const noUrl = !url;

  if (client === "claude") {
    return (
      <>
        <Steps
          items={[
            "Відкрий Claude: на сайті, у застосунку на компʼютері чи на телефоні.",
            <>
              Налаштування → <B>Connectors</B> → <B>Add custom connector</B>. На сайті налаштування називаються{" "}
              <B>Customize</B>.
            </>,
            <>
              <B>Name</B> — inFLOW, у поле з адресою встав своє посилання.
            </>,
            <>
              <B>Authentication</B> — лиши <B>No sign-in</B>: Claude вибирає його сам. Жовте попередження під ним
              означає, що посилання працює без пароля.
            </>,
            <>
              <B>Request headers</B> і <B>Advanced</B> не чіпай — просто натисни <B>Add</B>.
            </>,
            <>
              У чаті перевір, що inFLOW увімкнений: <B>+</B> → <B>Connectors</B>. Коли Claude спитає дозвіл на
              інструмент inFLOW, обери <B>Always allow</B>.
            </>,
          ]}
        />
        <div className="mt-4">
          <ClaudeConnectorMock url={url} />
        </div>
        <div className="mt-4">
          <ActionButton tone="secondary" onClick={() => openOutside("https://claude.ai/customize/connectors")}>
            Відкрити Claude
          </ActionButton>
        </div>
      </>
    );
  }
  if (client === "chatgpt") {
    return (
      <>
        <Steps
          items={[
            "Відкрий chatgpt.com у браузері на компʼютері. Потрібен тариф Plus, Pro або Business: у застосунку на телефоні підключення не додати.",
            <>
              Налаштування → <B>Security and login</B> → увімкни <B>Developer mode</B>. Якщо його там немає, шукай у{" "}
              <B>Apps</B> → <B>Advanced settings</B>.
            </>,
            <>
              Відкрий <B>Plugins</B> — кнопка нижче — і натисни <B>+</B>.
            </>,
            <>
              <B>Name</B> — inFLOW, <B>MCP Server URL</B> — твоє посилання, <B>Authentication</B> —{" "}
              <B>No authentication</B>.
            </>,
            <>
              Підтвердь, що довіряєш застосунку, і натисни <B>Create</B>.
            </>,
            <>
              У чаті натисни <B>+</B> → <B>Developer mode</B> і вибери inFLOW.
            </>,
          ]}
        />
        <div className="mt-4">
          <ChatGptAppMock url={url} />
        </div>
        <div className="mt-4">
          <ActionButton tone="secondary" onClick={() => openOutside("https://chatgpt.com/plugins")}>
            Відкрити ChatGPT
          </ActionButton>
        </div>
      </>
    );
  }
  if (client === "cursor") {
    return (
      <>
        <Steps
          items={[
            <>
              Відкрий Cursor → <B>Settings</B> → <B>MCP</B> → <B>Add new MCP server</B>.
            </>,
            "Встав налаштування нижче й збережи.",
          ]}
        />
        <CodeLine
          label="mcp.json"
          disabled={noUrl}
          code={JSON.stringify({ mcpServers: { inflow: { url: u } } }, null, 2)}
        />
      </>
    );
  }
  return (
    <>
      <div className="text-[0.8125rem] leading-snug text-ink [text-wrap:balance]">
        Будь-який застосунок із підтримкою MCP приймає це посилання як віддалений сервер типу HTTP.
      </div>
      <CodeLine label="Claude Code" disabled={noUrl} code={`claude mcp add --transport http inflow ${u}`} />
    </>
  );
}
